import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../utils/apiUrls";

function Profile() {
  const [profileData, setProfileData] = useState(null);
  const [showPickupForm, setShowPickupForm] = useState(false);
  const [isSubmittingPickup, setIsSubmittingPickup] = useState(false);
  const [editingPickupId, setEditingPickupId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pickupToDelete, setPickupToDelete] = useState(null);

  // use this when rendering rows as a stable key
  const getPickupKey = (loc, index) => loc.pickup_address_id || loc.pickupId || index;

  const INITIAL_PICKUP_DETAILS = {
    pickup_latitude: "",
    pickup_longitude: "",
    pickup_poc: "",
    pickup_organisation: "",
    pickup_address: "",
    pickup_district: "",
    pickup_city: "",
    pickup_state: "",
    pickup_landmark: "",
    pickup_poc_email: "",
    pickup_poc_contact_dialing_code: "",
    pickup_poc_contact_number: "",
    pickup_postal_code: "",
    pickup_country_code: "IN",
    pickup_address_type: "",
    pickup_hub_code: "",
    gst_number: "",
  };

  // All fields from the sample payload (6 mandatory fields starred)
  const [newPickupDetails, setNewPickupDetails] = useState({
    pickup_latitude: "",
    pickup_longitude: "",
    pickup_poc: "", // * mandatory
    pickup_organisation: "",
    pickup_address: "", // * mandatory
    pickup_district: "", // * mandatory
    pickup_city: "",
    pickup_state: "", // * mandatory
    pickup_landmark: "",
    pickup_poc_email: "",
    pickup_poc_contact_dialing_code: "",
    pickup_poc_contact_number: "", // * mandatory
    pickup_postal_code: "", // * mandatory
    pickup_country_code: "IN", // * mandatory
    pickup_address_type: "",
    pickup_hub_code: "",
    gst_number: "",
  });

  const authToken = localStorage.getItem("authToken");
  const parentClientId = localStorage.getItem("parentClientId");

  const fetchProfileData = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = API.getMerchantProfile(parentClientId);
      const response = await axios.get(apiUrl, {
        headers: {
          Accept: "application/json",
          Authorization: authToken,
        },
      });
      setProfileData(response.data);
    } catch (error) {
      console.error("Error fetching profile data:", error);
      toast.error("Failed to load profile data.");
    } finally {
      setLoading(false);
    }
  }, [authToken, parentClientId]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);


  // Handler to open the "Add Pickup Location" modal
  const openPickupModal = () => {
    setEditingPickupId(null);
    setNewPickupDetails(INITIAL_PICKUP_DETAILS);
    setShowPickupForm(true);
  };

  const closePickupModal = () => {
    setShowPickupForm(false);
    setEditingPickupId(null);
    setNewPickupDetails(INITIAL_PICKUP_DETAILS);
  };

  // Handler to update the "Pickup Location" modal
  const openEditPickupModal = (location) => {
    setEditingPickupId(location.pickup_address_id || location.pickupId || null);

    setNewPickupDetails({
      pickup_id: location.pickup_address_id || location.pickupId || null,
      pickup_latitude: location.pickup_latitude ?? "",
      pickup_longitude: location.pickup_longitude ?? "",
      pickup_poc: location.pickup_poc ?? "",
      pickup_organisation: location.pickup_organisation ?? "",
      pickup_address: location.pickup_address ?? "",
      pickup_district: location.pickup_district ?? "",
      pickup_city: location.pickup_city ?? "",
      pickup_state: location.pickup_state ?? "",
      pickup_landmark: location.pickup_landmark ?? "",
      pickup_poc_email: location.pickup_poc_email ?? "",
      pickup_poc_contact_dialing_code: location.pickup_poc_contact_dialing_code ?? "",
      pickup_poc_contact_number: location.pickup_poc_contact_number ?? "",
      pickup_postal_code: location.pickup_postal_code ?? "",
      pickup_country_code: location.pickup_country_code ?? "IN",
      pickup_address_type: location.pickup_address_type ?? "",
      pickup_hub_code: location.pickup_hub_code ?? "",
      gst_number: location.pickup_gst_number ?? location.gst_number ?? "",
    });

    setShowPickupForm(true);
  };

  // Handler for input changes in the new pickup form
  const handlePickupFieldChange = (e) => {
    const { name, value } = e.target;
    setNewPickupDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handler for submit pickup
  const handleSubmitPickup = async (e) => {
    e.preventDefault();

    const required = [
      "pickup_poc",
      "pickup_address",
      "pickup_district",
      "pickup_state",
      "pickup_postal_code",
      "pickup_poc_contact_number",
      "pickup_country_code",
      "pickup_city",
      "pickup_poc_email",
      "pickup_organisation",
    ];
    for (const k of required) {
      if (!newPickupDetails[k]) {
        toast.error("Please fill all mandatory fields (marked with *).");
        return;
      }
    }
    const merchantId = localStorage.getItem("merchantId");
    const apiUrl = API.addOrUpdatePickupDetails(merchantId);

    try {
      setIsSubmittingPickup(true);

      // ensure pickup_id present only when editing
      const payloadItem = { ...newPickupDetails };
      if (!editingPickupId) delete payloadItem.pickup_id;

      await axios.post(apiUrl, [payloadItem], {
        headers: {
          Authorization: localStorage.getItem("authToken"),
          "x-merchant-id": merchantId,
          "Content-Type": "application/json",
        },
      });

      toast.success(editingPickupId ? "Pickup updated successfully." : "Pickup added successfully.");
      setShowPickupForm(false);
      setEditingPickupId(null);
      setNewPickupDetails({
        pickup_latitude: "",
        pickup_longitude: "",
        pickup_poc: "",
        pickup_organisation: "",
        pickup_address: "",
        pickup_district: "",
        pickup_city: "",
        pickup_state: "",
        pickup_landmark: "",
        pickup_poc_email: "",
        pickup_poc_contact_dialing_code: "",
        pickup_poc_contact_number: "",
        pickup_postal_code: "",
        pickup_country_code: "IN",
        pickup_address_type: "",
        pickup_hub_code: "",
        gst_number: "",
      });
      fetchProfileData();
    } catch (err) {
      console.error(err);
      toast.error(editingPickupId ? "Failed to update pickup." : "Failed to add pickup.");
    } finally {
      setIsSubmittingPickup(false);
    }
  };

  const openDeleteConfirm = (pickupAddressId) => {
    setPickupToDelete(pickupAddressId);
    setShowDeleteConfirm(true);
  };

  const handleDeletePickup = async () => {
    const merchantId = localStorage.getItem("merchantId");
    const apiUrl = API.deletePickupDetails(merchantId, pickupToDelete);

    try {
      setDeletingId(pickupToDelete);
      await axios.delete(apiUrl, {
        headers: {
          Authorization: localStorage.getItem("authToken"),
          "x-merchant-id": merchantId,
        },
      });
      toast.success("Pickup location deleted successfully!");
      fetchProfileData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete pickup location.");
    } finally {
      setDeletingId(null);
      setShowDeleteConfirm(false);
      setPickupToDelete(null);
    }
  };

  // Filter pickup locations based on search
  const filteredPickupLocations = profileData?.pickup_details?.filter((location) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      location.pickup_organisation?.toLowerCase().includes(searchLower) ||
      location.pickup_address?.toLowerCase().includes(searchLower) ||
      location.pickup_city?.toLowerCase().includes(searchLower) ||
      location.pickup_state?.toLowerCase().includes(searchLower) ||
      location.pickup_poc_contact_number?.includes(searchQuery) ||
      location.pickup_poc_email?.toLowerCase().includes(searchLower)
    );
  }) || [];

  // Loading Skeleton Component
  const LoadingSkeleton = () => (
    <div className="animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="h-12 bg-gray-200 rounded"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen mt-2">
      <div className="max-w-[1920px] mx-auto px-4 lg:px-6 pb-4">
        {/* Header with KYC Status */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8"
        >
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              My Profile
            </h3>
            <p className="text-gray-600 text-sm sm:text-base">Manage your account information and settings</p>
          </div>
          
          {!loading && profileData && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {profileData?.basic_details?.aadhaar_verified ? (
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base shadow-lg flex items-center gap-2">
                  <span className="text-lg">✅</span>
                  <span>KYC Verified</span>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base shadow-lg flex items-center gap-2">
                  <span className="text-lg">❌</span>
                  <span>KYC Pending</span>
                </div>
              )}
            </motion.div>
          )}
        </motion.div>

        {loading ? (
          <LoadingSkeleton />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-4 sm:space-y-6"
          >
            {/* Account Status Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-blue-500"
            >
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div>
                  <h4 className="text-lg font-bold mb-2">Account Status</h4>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-white/20 px-2 py-1 rounded-full text-sm font-semibold">
                      {profileData?.status || 'PENDING'}
                    </span>
                    {profileData?.created_date_time && (
                      <span className="text-blue-100 text-sm">
                        Member since {new Date(profileData.created_date_time).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 w-full lg:w-auto">
                  {[
                    { label: 'Email', verified: profileData?.basic_details?.email_verified, icon: '📧' },
                    { label: 'Phone', verified: profileData?.basic_details?.phone_verified, icon: '📱' },
                    { label: 'Aadhaar', verified: profileData?.basic_details?.aadhaar_verified, icon: '🆔' },
                    { label: 'Bank', verified: profileData?.bank_details?.bank_verified, icon: '🏦' }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-2 sm:p-3">
                      <div className="text-lg sm:text-xl mb-1">{item.icon}</div>
                      <div className="text-xs text-blue-100">{item.label}</div>
                      <div className={`text-xs font-bold ${item.verified ? 'text-green-300' : 'text-orange-300'}`}>
                        {item.verified ? '✓ Verified' : '⚠ Pending'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Personal Information */}
            {(profileData?.basic_details?.name || profileData?.basic_details?.pan_number || profileData?.basic_details?.aadhaar_number || profileData?.basic_details?.gst_number) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
                className="bg-white shadow-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-2 sm:p-3 rounded-xl">
                    <span className="text-xl">🆔</span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">Personal Information</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {profileData?.basic_details?.name && (
                    <div className="group sm:col-span-2 lg:col-span-1">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">Full Name</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-500 text-lg">👨‍💼</span>
                        <input
                          type="text"
                          value={profileData.basic_details.name}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-purple-50 font-semibold text-gray-800 text-sm sm:text-base focus:outline-none"
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                  {profileData?.basic_details?.pan_number && (
                    <div className="group">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">PAN Number</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500 text-lg">🆔</span>
                        <input
                          type="text"
                          value={profileData.basic_details.pan_number}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-indigo-50 font-semibold text-gray-800 text-sm sm:text-base uppercase tracking-wider focus:outline-none"
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                  {profileData?.basic_details?.aadhaar_number && (
                    <div className="group">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">Aadhaar Number</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-lg">🔐</span>
                        <input
                          type="text"
                          value={`XXXX-XXXX-${profileData.basic_details.aadhaar_number.slice(-4)}`}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-blue-50 font-semibold text-gray-800 text-sm sm:text-base tracking-wider focus:outline-none"
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                  {profileData?.basic_details?.gst_number && (
                    <div className="group sm:col-span-2">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">GST Number</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 text-lg">📋</span>
                        <input
                          type="text"
                          value={profileData.basic_details.gst_number}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-green-50 font-semibold text-gray-800 text-sm sm:text-base uppercase tracking-wider focus:outline-none"
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                  {profileData?.basic_details?.msme_corporation_certificate_number && (
                    <div className="group">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">MSME Certificate</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-500 text-lg">🏭</span>
                        <input
                          type="text"
                          value={profileData.basic_details.msme_corporation_certificate_number}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-orange-50 font-semibold text-gray-800 text-sm sm:text-base focus:outline-none"
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Business Information Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white shadow-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 sm:p-3 rounded-xl">
                  <span className="text-xl">🏢</span>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Business Information</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="group sm:col-span-2 lg:col-span-1">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">Company Name</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-lg">🏢</span>
                    <input
                      type="text"
                      value={profileData?.basic_details?.brand_name || "N/A"}
                      className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-blue-50 font-semibold text-gray-800 text-sm sm:text-base focus:outline-none focus:border-blue-500 transition-all"
                      readOnly
                    />
                  </div>
                </div>
                <div className="group">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                    Contact Number
                    {profileData?.basic_details?.phone_verified && (
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">✓</span>
                    )}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 text-lg">📱</span>
                    <input
                      type="text"
                      value={profileData?.basic_details?.phone || "N/A"}
                      className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-green-50 font-semibold text-gray-800 text-sm sm:text-base focus:outline-none focus:border-green-500 transition-all"
                      readOnly
                    />
                  </div>
                </div>
                <div className="group sm:col-span-2 lg:col-span-1">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                    Email Address
                    {profileData?.basic_details?.email_verified && (
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">✓</span>
                    )}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-500 text-lg">📧</span>
                    <input
                      type="text"
                      value={profileData?.basic_details?.email || "N/A"}
                      className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-purple-50 font-semibold text-gray-800 text-sm sm:text-base focus:outline-none focus:border-purple-500 transition-all"
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Communication Address */}
            {profileData?.address_details?.communication_address && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white shadow-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 p-2 sm:p-3 rounded-xl">
                    <span className="text-xl">📍</span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">Communication Address</h2>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-4 sm:p-6">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-xl mt-1">🏢</span>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm sm:text-base">{profileData.address_details.communication_address.line1}</p>
                        {profileData.address_details.communication_address.line2 && (
                          <p className="text-gray-700 text-sm sm:text-base">{profileData.address_details.communication_address.line2}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-white px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <span>🏙️</span> {profileData.address_details.communication_address.city}
                      </span>
                      <span className="bg-white px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <span>🗺️</span> {profileData.address_details.communication_address.state}
                      </span>
                      <span className="bg-white px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <span>📮</span> {profileData.address_details.communication_address.pincode}
                      </span>
                      <span className="bg-white px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <span>🌍</span> {profileData.address_details.communication_address.country}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Bank Details Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white shadow-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-2 sm:p-3 rounded-xl">
                  <span className="text-xl">🏦</span>
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-gray-900">Bank Account Details</h2>
                  {profileData?.bank_details?.bank_verified !== undefined && (
                    <span className={`inline-block mt-1 text-xs px-3 py-1 rounded-full font-semibold ${
                      profileData.bank_details.bank_verified 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {profileData.bank_details.bank_verified ? '✓ Verified' : '⚠ Pending Verification'}
                    </span>
                  )}
                </div>
              </div>
              {profileData?.bank_details ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {profileData.bank_details.account_holder_name && (
                    <div className="sm:col-span-2 lg:col-span-1">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">Account Holder Name</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-lg">👤</span>
                        <input
                          type="text"
                          value={profileData.bank_details.account_holder_name}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-blue-50 font-semibold text-gray-800 text-sm sm:text-base"
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">Bank Name</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-lg">🏛️</span>
                      <input
                        type="text"
                        value={profileData.bank_details.bank_name || "N/A"}
                        className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-blue-50 font-semibold text-gray-800 text-sm sm:text-base"
                        readOnly
                      />
                    </div>
                  </div>
                  {profileData.bank_details.branch_name && (
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">Branch Name</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500 text-lg">🏢</span>
                        <input
                          type="text"
                          value={profileData.bank_details.branch_name}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-indigo-50 font-semibold text-gray-800 text-sm sm:text-base"
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">Account Number</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 text-lg">💳</span>
                      <input
                        type="text"
                        value={profileData.bank_details.account_number || "N/A"}
                        className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-green-50 font-semibold text-gray-800 text-sm sm:text-base tracking-wider"
                        readOnly
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">IFSC Code</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-500 text-lg">🔐</span>
                      <input
                        type="text"
                        value={profileData.bank_details.ifsc_code || "N/A"}
                        className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-purple-50 font-semibold text-gray-800 text-sm sm:text-base uppercase tracking-wider"
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  className="text-center py-8 sm:py-12"
                >
                  <div className="text-3xl mb-3">🏦</div>
                  <p className="text-gray-500 text-sm sm:text-base font-medium">No bank details added yet</p>
                  <p className="text-gray-400 text-xs sm:text-sm mt-1 sm:mt-2">Contact support to add bank information</p>
                </motion.div>
              )}
            </motion.div>

            {/* Pickup Locations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white shadow-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 p-2 sm:p-3 rounded-xl">
                    <span className="text-xl">📦</span>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Pickup Locations</h2>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                      {filteredPickupLocations.length} location{filteredPickupLocations.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openPickupModal}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold shadow-lg transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <span className="text-lg sm:text-xl">➕</span>
                  <span>Add Location</span>
                </motion.button>
              </div>

              {/* Search Bar */}
              {profileData?.pickup_details?.length > 0 && (
                <div className="mb-4 sm:mb-6">
                  <div className="relative">
                    <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-xl">🔍</span>
                    <input
                      type="text"
                      placeholder="Search by organization, address, city, phone..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-11 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                    />
                  </div>
                </div>
              )}

              {filteredPickupLocations.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {filteredPickupLocations.map((location, index) => {
                    const id = location.pickup_address_id || location.pickupId;
                    return (
                      <motion.div
                        key={getPickupKey(location, index)}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-gradient-to-br from-white to-blue-50 border-2 border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:shadow-xl hover:border-blue-300 transition-all duration-300 group"
                      >
                        {/* Card Header */}
                        <div className="flex items-start justify-between mb-3 sm:mb-4">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-sm sm:text-lg shadow-md">
                              {location.pickup_organisation?.charAt(0).toUpperCase() || "P"}
                            </div>
                            <div>
                              <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                                {location.pickup_organisation || "Unnamed"}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-500">{location.pickup_poc}</p>
                            </div>
                          </div>
                          <span className="bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full text-xs font-semibold">
                            📍 Active
                          </span>
                        </div>

                        {/* Address */}
                        <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-white rounded-lg border border-gray-100">
                          <div className="flex items-start gap-2 mb-2">
                            <span className="text-base sm:text-lg">🏢</span>
                            <div className="flex-1">
                              <p className="text-xs sm:text-sm font-medium text-gray-700 leading-relaxed">
                                {location.pickup_address}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {location.pickup_city}, {location.pickup_state} - {location.pickup_postal_code}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Contact Info */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-4">
                          <div className="flex items-center gap-2 p-2 sm:p-3 bg-green-50 rounded-lg">
                            <span className="text-base sm:text-lg">📱</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs text-gray-500">Phone</p>
                              <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                                {location.pickup_poc_contact_number}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 p-2 sm:p-3 bg-purple-50 rounded-lg">
                            <span className="text-base sm:text-lg">📧</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                                {location.pickup_poc_email || "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 sm:gap-3">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => openEditPickupModal(location)}
                            className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-semibold transition-all shadow-md flex items-center justify-center gap-2 text-xs sm:text-sm"
                          >
                            <span>✏️</span>
                            <span>Edit</span>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => openDeleteConfirm(id)}
                            disabled={deletingId === id}
                            className="flex-1 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-semibold transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-xs sm:text-sm"
                          >
                            <span>🗑️</span>
                            <span>{deletingId === id ? "Deleting..." : "Delete"}</span>
                          </motion.button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : profileData?.pickup_details?.length > 0 && searchQuery ? (
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="text-3xl mb-4">🔍</div>
                  <p className="text-gray-500 text-lg font-medium">No locations found</p>
                  <p className="text-gray-400 text-sm mt-2">Try searching with different keywords</p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSearchQuery("")}
                    className="mt-4 text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    Clear search
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="text-3xl mb-4">📦</div>
                  <p className="text-gray-500 text-lg font-medium">No pickup locations yet</p>
                  <p className="text-gray-400 text-sm mt-2">Add your first pickup location to get started</p>
                </motion.div>
              )}
            </motion.div>

            {/* Documents */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white shadow-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-2 sm:p-3 rounded-xl">
                  <span className="text-xl">📜</span>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Documents</h2>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                    {profileData?.documents?.length || 0} document{profileData?.documents?.length !== 1 ? 's' : ''} uploaded
                  </p>
                </div>
              </div>
              {profileData?.documents?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {profileData.documents.map((doc, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-gradient-to-br from-white to-purple-50 border-2 border-gray-200 rounded-xl p-4 hover:shadow-lg hover:border-purple-300 transition-all duration-300 group"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-2 rounded-lg">
                          <span className="text-lg">📄</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 truncate group-hover:text-purple-600 transition-colors">
                            {doc.file_name}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1">
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">
                              {doc.document_type}
                            </span>
                          </p>
                        </div>
                      </div>
                      <motion.a
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        href={doc.s3_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-lg font-semibold text-center transition-all shadow-md text-sm"
                      >
                        <span className="mr-2">👁️</span>
                        View Document
                      </motion.a>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="text-3xl mb-4">📜</div>
                  <p className="text-gray-500 text-lg font-medium">No documents uploaded</p>
                  <p className="text-gray-400 text-sm mt-2">Documents will appear here once uploaded</p>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowDeleteConfirm(false);
              setPickupToDelete(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8"
            >
              <div className="text-center">
                <div className="bg-red-100 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <span className="text-3xl">🗑️</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Pickup Location?</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                  This action cannot be undone. Are you sure you want to permanently delete this pickup location?
                </p>
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setPickupToDelete(null);
                    }}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold transition-all text-sm sm:text-base"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDeletePickup}
                    disabled={deletingId}
                    className="flex-1 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold transition-all disabled:opacity-50 text-sm sm:text-base"
                  >
                    {deletingId ? "Deleting..." : "Delete"}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Pickup Location Modal */}
      <AnimatePresence>
        {showPickupForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4"
            onClick={closePickupModal}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-5xl relative max-h-[95vh] overflow-hidden"
            >
              {/* Modal Header - Sticky */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 p-4 sm:p-6 z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-white mb-1">
                      {editingPickupId ? "✏️ Edit Pickup Location" : "➕ Add Pickup Location"}
                    </h2>
                    <p className="text-blue-100 text-xs sm:text-sm">
                      Fields marked with <span className="text-red-300">*</span> are required
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={closePickupModal}
                    className="bg-white/20 hover:bg-white/30 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold transition-colors"
                    aria-label="Close modal"
                  >
                    ×
                  </motion.button>
                </div>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="overflow-y-auto max-h-[calc(95vh-200px)] p-4 sm:p-6 lg:p-8 custom-scrollbar">
                <form onSubmit={handleSubmitPickup} id="pickup-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {/* Pickup POC */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Contact Person <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">👤</span>
                        <input
                          type="text"
                          name="pickup_poc"
                          value={newPickupDetails.pickup_poc}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="John Doe"
                          required
                        />
                      </div>
                    </div>

                    {/* Organization */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Organization Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🏢</span>
                        <input
                          type="text"
                          name="pickup_organisation"
                          value={newPickupDetails.pickup_organisation}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="Acme Corp"
                          required
                        />
                      </div>
                    </div>

                    {/* Address - Full Width */}
                    <div className="sm:col-span-2">
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Street Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-4 text-gray-400 text-lg">📍</span>
                        <textarea
                          name="pickup_address"
                          value={newPickupDetails.pickup_address}
                          onChange={handlePickupFieldChange}
                          rows={2}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none text-sm sm:text-base"
                          placeholder="123 Main Street, Building A, Floor 2"
                          required
                        />
                      </div>
                    </div>

                    {/* City */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        City <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🏙️</span>
                        <input
                          type="text"
                          name="pickup_city"
                          value={newPickupDetails.pickup_city}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="Mumbai"
                          required
                        />
                      </div>
                    </div>

                    {/* District */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        District <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🗺️</span>
                        <input
                          type="text"
                          name="pickup_district"
                          value={newPickupDetails.pickup_district}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="Mumbai Suburban"
                          required
                        />
                      </div>
                    </div>

                    {/* State */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        State <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🌏</span>
                        <input
                          type="text"
                          name="pickup_state"
                          value={newPickupDetails.pickup_state}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="Maharashtra"
                          required
                        />
                      </div>
                    </div>

                    {/* Postal Code */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Postal Code <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">📮</span>
                        <input
                          type="text"
                          name="pickup_postal_code"
                          value={newPickupDetails.pickup_postal_code}
                          onChange={handlePickupFieldChange}
                          maxLength={6}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="400001"
                          required
                        />
                      </div>
                    </div>

                    {/* Landmark */}
                    <div className="sm:col-span-2">
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Landmark (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🏛️</span>
                        <input
                          type="text"
                          name="pickup_landmark"
                          value={newPickupDetails.pickup_landmark}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="Near City Mall"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">📱</span>
                        <input
                          type="text"
                          name="pickup_poc_contact_number"
                          value={newPickupDetails.pickup_poc_contact_number}
                          onChange={handlePickupFieldChange}
                          maxLength={10}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="9876543210"
                          required
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">📧</span>
                        <input
                          type="email"
                          name="pickup_poc_email"
                          value={newPickupDetails.pickup_poc_email}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="contact@example.com"
                          required
                        />
                      </div>
                    </div>

                    {/* Country Code */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Country Code <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🌍</span>
                        <input
                          type="text"
                          name="pickup_country_code"
                          value="IN"
                          readOnly
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    {/* Dialing Code */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Dialing Code (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">☎️</span>
                        <input
                          type="text"
                          name="pickup_poc_contact_dialing_code"
                          value={newPickupDetails.pickup_poc_contact_dialing_code}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="+91"
                        />
                      </div>
                    </div>

                    {/* Latitude */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Latitude (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🧭</span>
                        <input
                          type="number"
                          step="any"
                          name="pickup_latitude"
                          value={newPickupDetails.pickup_latitude}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="19.0760"
                        />
                      </div>
                    </div>

                    {/* Longitude */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Longitude (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🧭</span>
                        <input
                          type="number"
                          step="any"
                          name="pickup_longitude"
                          value={newPickupDetails.pickup_longitude}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="72.8777"
                        />
                      </div>
                    </div>

                    {/* Address Type */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Address Type (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🏷️</span>
                        <input
                          type="text"
                          name="pickup_address_type"
                          value={newPickupDetails.pickup_address_type}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="Warehouse"
                        />
                      </div>
                    </div>

                    {/* Hub Code */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Hub Code (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🏭</span>
                        <input
                          type="text"
                          name="pickup_hub_code"
                          value={newPickupDetails.pickup_hub_code}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base"
                          placeholder="HUB001"
                        />
                      </div>
                    </div>

                    {/* GST Number */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        GST Number (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">📋</span>
                        <input
                          type="text"
                          name="gst_number"
                          value={newPickupDetails.gst_number}
                          onChange={handlePickupFieldChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm sm:text-base uppercase tracking-wider"
                          placeholder="29ABCDE1234F1Z5"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Modal Footer - Sticky */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 sm:p-6 flex gap-3">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={closePickupModal}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-semibold transition-all text-sm sm:text-base"
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  form="pickup-form"
                  whileHover={{ scale: isSubmittingPickup ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmittingPickup ? 1 : 0.98 }}
                  disabled={isSubmittingPickup}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm sm:text-base flex items-center justify-center gap-2"
                >
                  {isSubmittingPickup ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      {editingPickupId ? "Saving..." : "Adding..."}
                    </>
                  ) : (
                    <>
                      {editingPickupId ? "💾 Save Changes" : "➕ Add Pickup"}
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Profile;
